import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import { persistState } from '@ngneat/elf-persist-state';
import { createRequestDataSource, createRequestsStatusOperator, getRequestStatus, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { AppError, AuthTokenDto, PaymentTypeValues, UpdateEmailRequest, UpdateEmailResult } from '@timechef/core/api';
import { isSameDay } from 'date-fns';
import { map } from 'rxjs';
import { AppErrorKeys, appErrorFromKey } from '../errors/error';
import { capacitorStorageStrategy, includeKeys, selectAppRequestStatus } from '../utils/elf-utils';
import { UpdateAccountInfoForm, User } from './auth.models';
import { userFullName, userInitials } from './auth.utils';

interface AuthProps {
  authTokens: AuthTokenDto | null;
  user: User | null;
  lastUserFetch?: Date;
  activeSiteIdx: number | null;
  isLoggedInWithSSo: boolean;
  lastPushRegister: Date | null;
  redirectUrlAfterNextLogin: string | null;
}

export enum AuthStatusKeys {
  Login = 'login',
  CheckEmail = 'check-email',
  LoadUser = 'load-user',
  AccountBalance = 'account-balance',
  ForgotPassword = 'forgot-password',
  ResetPassword = 'reset-password',
  ChangePassword = 'change-password',
  UpdateAccountInfos = 'update-account-infos',
  UpdateAccountEmail = 'update-account-email',
  UpdateBalanceAlert = 'update-balance-alert',
  UpdateOptInSettings = 'update-opt-in-settings',
}

@Injectable({ providedIn: 'root' })
export class AuthRepository {
  private store = createStore(
    { name: 'auth' },
    withProps<AuthProps>({
      authTokens: null,
      user: null,
      activeSiteIdx: null,
      isLoggedInWithSSo: false,
      lastPushRegister: null,
      redirectUrlAfterNextLogin: null,
    }),
    withRequestsStatus<AuthStatusKeys>(),
  );

  instance = persistState(this.store, {
    key: 'auth',
    storage: capacitorStorageStrategy,
    source: () => this.store.pipe(includeKeys(['activeSiteIdx', 'authTokens', 'isLoggedInWithSSo', 'user'])),
  });

  userOptInSettingsDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        select((s) => {
          return { acceptEliorOffers: s.user?.acceptEliorOffers, acceptPartnersOffers: s.user?.acceptPartnersOffers };
        }),
      ),
    requestKey: AuthStatusKeys.UpdateOptInSettings,
    dataKey: 'data',
  });

  authTokens$ = this.store.pipe(select((s) => s.authTokens));
  currentSiteName$ = this.store.pipe(select((s) => s.user?.sites![s.activeSiteIdx!].libelleLong));
  userPaymentType$ = this.store.pipe(select((s) => s.user?.sites![s.activeSiteIdx!].paymentType));
  currentSite$ = this.store.pipe(
    select((s) => s.user?.sites![s.activeSiteIdx!]),
    map((x) => {
      if (x) {
        // New properties in UserSiteDto, let's handle the old version
        if (!x.hasOwnProperty('showBooking')) {
          x.showBooking = true;
        }

        if (!x.hasOwnProperty('restaurantsDisplayed')) {
          x.restaurantsDisplayed = true;
        }
      }
      return x;
    }),
  );
  restaurantDisplayed$ = this.store.pipe(select((s) => s.user?.sites![s.activeSiteIdx!].restaurantsDisplayed));

  qrCodeInfo$ = this.store.pipe(select((s) => s.user?.sites![s.activeSiteIdx!].qrCodeInformation));
  currentSiteBookingBoundsDays$ = this.store.pipe(
    select((s) => {
      return {
        bookingMaxDays: s.user?.sites![s.activeSiteIdx!].bookingMaxDays,
        bookingMinDays: s.user?.sites![s.activeSiteIdx!].bookingMinDays,
        bookingOpenedDays: s.user?.sites![s.activeSiteIdx!].bookingOpenedDays,
      };
    }),
  );
  userId$ = this.store.pipe(select((s) => s.user?.id ?? null));
  userEmail$ = this.store.pipe(select((s) => s.user?.email ?? ''));
  userFullName$ = this.store.pipe(select((s) => (s.user ? userFullName(s.user) : '')));
  userInitials$ = this.store.pipe(select((s) => (s.user ? userInitials(s.user) : '')));
  userWithCashRegister$ = this.store.pipe(select((s) => isUserWithCashRegister(s)));
  userHasBalanceAlert$ = this.store.pipe(
    select((s) => {
      const site = s.user!.sites![s.activeSiteIdx!];
      return site.balanceAlertSiteIsActive && isUserWithCashRegister(s) && site.paymentType != PaymentTypeValues.PostPaiement;
    }),
  );
  loginRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.Login));
  checkEmailRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.CheckEmail));
  forgotPasswordRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.ForgotPassword));
  resetPasswordRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.ResetPassword));
  changePasswordRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.ChangePassword));
  updateAccountInfosRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.UpdateAccountInfos));
  updateAccountEmailRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.UpdateAccountEmail));
  updateBalanceAlertRequestStatus$ = this.store.pipe(selectAppRequestStatus(AuthStatusKeys.UpdateBalanceAlert));

  isLoggedIn$ = this.store.pipe(
    select((s) => {
      return !!s.authTokens && !!s.user;
    }),
  );

  hasPhoneNumber$ = this.store.pipe(
    select((s) => {
      return !!s.user?.phoneNumber;
    }),
  );

  accountBalanceDS = createRequestDataSource({
    store: this.store,
    data$: () => this.store.pipe(select((s) => s.user?.sites[s.activeSiteIdx!].solde)),
    requestKey: AuthStatusKeys.AccountBalance,
    dataKey: 'data',
  });

  getAuthTokens = () => this.store.query((s) => s.authTokens);
  getLoginRequestStatus = () => this.store.query(getRequestStatus(AuthStatusKeys.Login));
  getAccountBalance = () => this.store.query((s) => s.user?.sites[s.activeSiteIdx!].solde);
  getUserPaymentType = () => this.store.query((s) => s.user?.sites[s.activeSiteIdx!].paymentType);
  getAccessToken = () => this.store.query((s) => s.authTokens?.accessToken);
  getHasRegisteredToPushRecently = () => this.store.query((s) => s.lastPushRegister && isSameDay(new Date(), s.lastPushRegister));
  getCurrentSiteName = () => this.store.query((s) => s.user!.sites![s.activeSiteIdx!].name!);
  getCurrentSitePresenceHeureArrivee = () => this.store.query((s) => s.user!.sites![s.activeSiteIdx!].askPresenceOnSiteHeureArrivee!);
  getUser = () => this.store.query((s) => s.user);
  getUserEmail = () => this.store.query((s) => s.user!.email);
  getUserFullName = () => this.store.query((s) => userFullName(s.user!));
  getIsLoggedIn = () => this.store.query((s) => !!s.authTokens);
  getRefreshToken = () => this.store.query((s) => s.authTokens?.refreshToken);
  getCurrentUserSite = () => this.store.query((s) => s.user?.sites![s.activeSiteIdx!]);
  getIsUserWithCashRegister = () => this.store.query((s) => isUserWithCashRegister(s));
  getRedirectUrlAfterNextLogin = () => this.store.query((s) => s.redirectUrlAfterNextLogin);

  getBalanceAlert = () =>
    this.store.query((s) => {
      const site = s.user!.sites![s.activeSiteIdx!];
      return {
        userAmount: s.user?.balanceAlertAmount,
        maxAmount: site.balanceAlertMaximumAmount,
        siteAmount: site.balanceAlertSiteAmount,
      };
    });

  updateBalanceAlertAmount(amount: number): void {
    this.store.update(
      (s) => ({
        ...s,
        user: { ...s.user!, balanceAlertAmount: amount },
      }),
      updateRequestStatus(AuthStatusKeys.UpdateBalanceAlert, 'success'),
    );
  }

  login(authTokens: AuthTokenDto, isLoggedInWithSSo = false): void {
    this.store.update(setProps({ authTokens, isLoggedInWithSSo }), updateRequestStatus(AuthStatusKeys.Login, 'success'));
  }

  loginFailed(key: AppErrorKeys) {
    this.store.update(updateRequestStatus(AuthStatusKeys.Login, 'error', appErrorFromKey(key)));
  }

  cancelLogin() {
    this.store.update(updateRequestStatus(AuthStatusKeys.Login, 'idle'));
  }

  resetCheckEmailStatus(): void {
    this.store.update(updateRequestStatus(AuthStatusKeys.CheckEmail, 'idle'));
  }

  setLastPushRegister(lastPushRegister: Date | null) {
    this.store.update(setProps({ lastPushRegister }));
  }

  overrideLoginError(err: AppError) {
    this.store.update(updateRequestStatus(AuthStatusKeys.Login, 'error', err));
  }

  forgotPassword(): void {
    this.store.update(updateRequestStatus(AuthStatusKeys.ForgotPassword, 'success'));
  }

  cancelForgotPassword(): void {
    this.store.update(updateRequestStatus(AuthStatusKeys.ForgotPassword, 'idle'));
  }

  resetPassword(): void {
    this.store.update(updateRequestStatus(AuthStatusKeys.ResetPassword, 'success'));
  }

  resetUpdateAccountEmailStatus(): void {
    this.store.update(updateRequestStatus(AuthStatusKeys.UpdateAccountEmail, 'idle'));
  }

  changePassword(authTokens: AuthTokenDto): void {
    this.store.update(setProps({ authTokens }), updateRequestStatus(AuthStatusKeys.ChangePassword, 'success'));
  }

  refreshAccountBalance(balance?: number) {
    this.store.update(
      (s) => ({
        ...s,
        user: { ...s.user!, sites: { ...s.user!.sites, [s.activeSiteIdx!]: { ...s.user!.sites[s.activeSiteIdx!], solde: balance } } },
      }),
      this.accountBalanceDS.setSuccess(),
    );
  }

  setUser(user: User, statusKey: AuthStatusKeys): void {
    this.store.update(
      setProps({ user, activeSiteIdx: 0, lastUserFetch: new Date() }),
      this.accountBalanceDS.setSuccess(),
      // User Info Request status is currently linked to login
      updateRequestStatus(statusKey, 'success'),
    );
  }

  updateAccountInfos(update: UpdateAccountInfoForm, authTokens: AuthTokenDto) {
    this.store.update(
      (s) => ({
        ...s,
        authTokens,
        user: { ...s.user!, firstName: update.firstName, lastName: update.lastName, phoneNumber: update.phoneNumber },
      }),
      updateRequestStatus(AuthStatusKeys.UpdateAccountInfos, 'success'),
    );
  }

  updateOptInSettings(acceptEliorOffers?: boolean, acceptPartnersOffers?: boolean) {
    this.store.update(
      (s) => ({
        ...s,
        user: {
          ...s.user!,
          acceptEliorOffers: acceptEliorOffers ?? s.user!.acceptEliorOffers,
          acceptPartnersOffers: acceptPartnersOffers ?? s.user!.acceptPartnersOffers,
        },
      }),
      this.userOptInSettingsDS.setSuccess(),
    );
  }

  resetUpdateAccountInfosStatus() {
    this.store.update(updateRequestStatus(AuthStatusKeys.UpdateAccountInfos, 'idle'));
  }

  updateAccountEmail(request: UpdateEmailRequest, result: UpdateEmailResult) {
    this.store.update(
      (s) => ({ ...s, authTokens: result.authToken, user: { ...s.user!, email: request.email } }),
      updateRequestStatus(AuthStatusKeys.UpdateAccountEmail, 'success'),
    );
  }

  setRedirectUrlAfterNextLogin(redirectUrl: string | null) {
    this.store.update((s) => ({ ...s, redirectUrlAfterNextLogin: redirectUrl }));
  }

  public trackRequestStatus = createRequestsStatusOperator(this.store);
}

const isUserWithCashRegister = (state: AuthProps) => {
  // User not logged in, there is no cash register
  if (!state.user) return false;

  const site = state.user.sites![state.activeSiteIdx!];

  return site.hasCashRegister && !site.registrationWithoutCashRegister;
};
